import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Col,
  Row,
  Checkbox,
  Button,
  message,
  Select,
  Alert,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { validateMobileNumber } from '../../Validators/ValidatorMobileNumber';
import { validateEmail } from '../../Validators/ValidatorEmail';
import {
  validateAtLeastOneStudent,
  validateCompanyName,
} from '../../Validators/ValidatorOrganization';

import '../../Assets/Styles/Form.module.css';
import {
  GetCurrentStudyYear,
  GetOrganizations,
  PostOrganization,
} from '../../helper/apiHelper';
import type { StudyYear } from '../../Types/interfaces';
import { PaperWorkLanguage, type Organization } from '../../Types/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useProcessStartSettings } from '../../hooks/useProcessStartSettings';
import { ProcessBlockedBanner } from '../ProcessBlockedBanner';

const { TextArea } = Input;

interface OrganizationFormValues {
  representativeName: string;
  name: string;
  description: string;
  city: string;
  county: string;
  street: string;
  streetNumber: string;
  phoneNumber: string;
  email: string;
  fax: string;
  fiscalCode: string;
  fiscalAccount: string;
  iban: string;
  isRomanian: boolean;
  bachelorStudents: string;
  masterStudents: string;
  paperWorkType: PaperWorkLanguage;
}

const OrganizationFormComponent = (): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const [studyYear, setStudyYear] = useState<StudyYear | null>();

  useEffect(() => {
    const fetchData = async () => {
      const resp = await GetCurrentStudyYear();
      setStudyYear(resp ? resp : null);
    };
    fetchData();
  }, []);

  const postOrganizationMutation = useMutation({
    mutationFn: PostOrganization,
    onSuccess: () => {
      form.resetFields();
      messageApi.success({
        content: t('orgRegComp.regSuccess'),
      });

      // invalidate organization cached data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORGANIZATIONS,
      });

      // invalidate user cached data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER,
      });
    },
    onError: () => {
      messageApi.error({
        content: t('orgRegComp.regError'),
      });
    },
  });

  const { data: organizations } = useQuery({
    queryKey: [...QUERY_KEYS.ORGANIZATIONS],
    queryFn: async () => {
      return await GetOrganizations();
    },
    initialData: [],
  });

  const handleSubmit = async (org: OrganizationFormValues): Promise<void> => {
    if (!studyYear) {
      return;
    }

    const organization: Organization = {
      representativeId: loggedInUser?.id ?? 0,
      representativeName: org.representativeName,
      name: org.name,
      description: org.description,
      city: org.city,
      county: org.county,
      street: org.street,
      streetNumber: org.streetNumber,
      phoneNumber: org.phoneNumber,
      email: org.email,
      fax: org.fax,
      fiscalCode: org.fiscalCode,
      fiscalAccount: org.fiscalAccount,
      iban: org.iban,
      isRomanian: org.isRomanian === undefined || !!org.isRomanian,
      bachelorStudents: parseInt(org.bachelorStudents) || 0,
      masterStudents: parseInt(org.masterStudents) || 0,
      language: org.paperWorkType,
      studyYearId: studyYear.id,
    };

    form.resetFields();

    // update organizations
    await postOrganizationMutation.mutateAsync(organization);
  };

  const { companyRegistrationEnabled } = useProcessStartSettings();

  return (
    <>
      {contextHolder}
      {!companyRegistrationEnabled && (
        <ProcessBlockedBanner
          style={{ marginBottom: 16 }}
          message={t('processStartSettings.companyRegistrationBlocked')}
        />
      )}
      {studyYear === null && (
        <Alert
          message={t('orgRegComp.alertMessage')}
          description={t('orgRegComp.alertDescription')}
          type="error"
          showIcon
        />
      )}
      <Form
        layout="horizontal"
        size="large"
        onFinish={handleSubmit}
        form={form}
        style={{ paddingTop: 10 }}
      >
        <Col>
          <Row justify="space-between" gutter={10}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.companyNameNeeded'),
                  },
                  {
                    validator: (rule, value) =>
                      validateCompanyName(rule, value, organizations),
                  },
                ]}
              >
                <Input placeholder={t('orgRegComp.orgName')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="representativeName"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.representativeNameNeeded'),
                  },
                ]}
              >
                <Input placeholder={t('orgRegComp.representativeName')} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between" gutter={10}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="city"
                rules={[
                  { required: true, message: t('orgRegComp.cityNeeded') },
                ]}
              >
                <Input placeholder={t('orgRegComp.city')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="county"
                rules={[
                  { required: true, message: t('orgRegComp.countyNeeded') },
                ]}
              >
                <Input placeholder={t('orgRegComp.county')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="street"
                rules={[
                  { required: true, message: t('orgRegComp.streetNeeded') },
                ]}
              >
                <Input placeholder={t('orgRegComp.street')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="streetNumber"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.numberNeeded'),
                  },
                ]}
              >
                <Input placeholder={t('orgRegComp.streetNr')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            rules={[
              { required: true, message: t('orgRegComp.descriptionNeeded') },
              { min: 20, message: t('orgRegComp.descriptionMinLength') },
            ]}
          >
            <TextArea rows={4} placeholder={t('orgRegComp.desc')} />
          </Form.Item>
          <Row justify="space-between" gutter={10}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="phoneNumber"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.phoneNeeded'),
                  },
                  { validator: validateMobileNumber },
                ]}
              >
                <Input placeholder={t('orgRegComp.phone')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="fax"
                rules={[{ message: t('orgRegComp.faxNeeded') }]}
              >
                <Input placeholder={t('orgRegComp.fax')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.emailNeeded'),
                  },
                  { validator: validateEmail },
                ]}
              >
                <Input placeholder={t('orgRegComp.email')} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between" gutter={10}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="fiscalCode"
                rules={[
                  { required: true, message: t('orgRegComp.fiscalCodeNeeded') },
                ]}
              >
                <Input placeholder={t('orgRegComp.fisc')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="fiscalAccount"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.bankAccountNeeded'),
                  },
                ]}
              >
                <Input placeholder={t('orgRegComp.account')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="iban"
                rules={[
                  { required: true, message: t('orgRegComp.ibanNeeded') },
                ]}
              >
                <Input placeholder={t('orgRegComp.iban')} />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="space-between" gutter={10}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="bachelorStudents"
                dependencies={['masterStudents']}
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.bachelorStudentsNeeded'),
                  },
                  {
                    type: 'number',
                    min: 0,
                    message: t('orgRegComp.nonNegativeNumber'),
                  },
                  validateAtLeastOneStudent('masterStudents', t),
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder={t('orgRegComp.bachelorStudents')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="masterStudents"
                dependencies={['bachelorStudents']}
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.masterStudentsNeeded'),
                  },
                  {
                    type: 'number',
                    min: 0,
                    message: t('orgRegComp.nonNegativeNumber'),
                  },
                  validateAtLeastOneStudent('bachelorStudents', t),
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder={t('orgRegComp.masterStudents')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="paperWorkType"
                rules={[
                  {
                    required: true,
                    message: t('orgRegComp.languageRequired'),
                  },
                ]}
              >
                <Select
                  placeholder={t('orgRegComp.paperWorkLanguage')}
                  options={Object.values(PaperWorkLanguage)
                    .filter((val) => typeof val !== 'number')
                    .map((value) => ({
                      label: value,
                      value,
                    }))}
                ></Select>
              </Form.Item>
            </Col>
            <Col span={7}></Col>
          </Row>

          <Row justify="space-between">
            <Col span={7}>
              <Form.Item name="isRomanian" valuePropName="checked">
                <Checkbox>{t('orgRegComp.fromRom')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={postOrganizationMutation.isPending}
              disabled={studyYear === null || !companyRegistrationEnabled}
            >
              {t('orgRegComp.register')}
            </Button>
          </Form.Item>
        </Col>
      </Form>
    </>
  );
};

export default OrganizationFormComponent;
